import type { LucideIcon } from "lucide-react";
import { Compass, Hotel, Map, MapPin, Users, Volume2 } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Container } from "../components/Container";
import { SectionHeading } from "../components/SectionHeading";

type FeatureKey =
  | "audio_guides"
  | "itineraries"
  | "hidden_gems"
  | "tours"
  | "stays"
  | "social";

const FEATURE_ICONS: Record<FeatureKey, LucideIcon> = {
  audio_guides: Volume2,
  itineraries:  Map,
  hidden_gems:  MapPin,
  tours:        Compass,
  stays:        Hotel,
  social:       Users,
};

const FEATURE_KEYS: ReadonlyArray<FeatureKey> = [
  "audio_guides",
  "itineraries",
  "hidden_gems",
  "tours",
  "stays",
  "social",
];

export function Features() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  return (
    <section id="features" className="py-14 sm:py-20">
      <Container>
        <SectionHeading
          eyebrow={t("features.eyebrow")}
          title={t("features.section_title")}
          description={t("features.section_description")}
        />

        <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURE_KEYS.map((key, i) => {
            const Icon = FEATURE_ICONS[key];
            return (
              <motion.li
                key={key}
                initial={reduce ? false : { opacity: 0, y: 24 }}
                whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.5,
                  ease: "easeOut",
                  delay: i * 0.05,
                }}
                whileHover={reduce ? undefined : { y: -4 }}
                className="group rounded-xl border border-transparent bg-bg-muted p-8 transition-shadow duration-200 hover:shadow-xl dark:border-border-soft dark:bg-bg-subtle"
              >
                <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500 dark:bg-brand-950">
                  <Icon className="h-7 w-7" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-text-heading">
                  {t(`features.${key}.title`)}
                </h3>
                <p className="mt-2 text-sm text-text-muted">
                  {t(`features.${key}.description`)}
                </p>
              </motion.li>
            );
          })}
        </ul>
      </Container>
    </section>
  );
}
